/**
 * A Documents task's documents (Duncan 2026-10-24; E3 Subs PLAN v1.17): O&M manuals, as-builts, training records,
 * warranties, submittals -- five built-in folders and the folders people add, nested -- kept on E3 Subs, the sub's and
 * E3's in one list (Summit keeps no copy). Finder-style columns, as the Closeout tab and E3 Subs show them (his pick):
 * the folders, then each open folder's contents, then the picked file. A PM uploads here too ("the project manager
 * could upload it from that spot as well"): any size up to 20 GB, in 8 MB pieces from this browser straight to E3 Subs,
 * carrying on after a break. Anyone who can open the job removes any document or folder (Duncan): it goes to E3 Subs'
 * recycle bin, where E3's admins can restore it for 365 days. Server side: routes/taskWork.js.
 * Duncan 2026-10-25, the sub's tools here too: Rename and Move a file, and Upload folder (its subfolders made to match,
 * as the Closeout tab and E3 Subs do) -- the same rules as E3 Subs: 300 files at a time, folders 8 deep, names 80
 * characters, no hidden files, and empty or runnable files left out and named.
 * Duncan 2026-10-25: the documents' progress is a slider, 0-100 in steps of 5, shared with the sub -- whoever moves it
 * last -- with who set it and when. It's not the task's % done (what bills): that stays the PM's, on the Schedule.
 * Duncan 2026-10-26 (as on E3 Subs' card): the slider says what to do with it and what it does, with marks under it.
 * Duncan 2026-10-25, to Closeout: someone who manages closeout documents ticks files and folders here, then Send to
 * Closeout… picks the job's Closeout folder they go to (SendToCloseout.js).
 * Duncan 2026-10-25 (design v4, the mockup he OK'd): the task's recycle bin, from a link at the end of the toolbar, in
 * the preview's place -- Restore for admins, VPs and accounting (subs.restore) -- and photo reports tagged "Auto".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getTaskDocuments, startTaskDocument, finishTaskDocument, removeTaskDocument, renameTaskDocument, moveTaskDocument,
  addTaskFolder, renameTaskFolder, removeTaskFolder, putDocumentPiece, setTaskDocumentsPercent, getTaskBin, restoreFromTaskBin,
} from '../../api';
import { ErrorBanner, SecondaryButton, fieldStyle } from './SubUi';
import { newRequestId } from './LineWork';
import { useTicks, useCloseoutSend, AlreadyThere, SendProgress, CloseoutFolderPicker, SEND } from './SendToCloseout';

const COL_W = 236;
const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
// A photo report (E3 Subs makes one per finished line) is tagged "Auto", as on E3 Subs (Duncan 2026-10-25).
const AutoTag = () => (
  <span title="A photo report E3 Subs made from the line's work" style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 650, letterSpacing: '0.03em',
    padding: '1px 6px', borderRadius: 5, color: 'var(--accent-text)', background: 'var(--accent-soft)' }}>Auto</span>
);

const Glyph = ({ children, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>{children}</svg>
);
const FOLDER = <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.8a1.5 1.5 0 0 1 1.1.5l1.2 1.3a1.5 1.5 0 0 0 1.1.5H19A1.5 1.5 0 0 1 20.5 9v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z" />;
const FILE = <><path d="M6.5 3.5h7l4 4v12a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1Z" /><path d="M13.5 3.5v4h4" /><path d="M8.5 12.5h7M8.5 16h5" /></>;
const CHEVRON = <path d="M9 6l6 6-6 6" />;
const TRASH = <path d="M4 7h16M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />;
// E3 Subs' rules (apps/web/src/lib/uploads.ts, components/Documents.tsx), so a file goes up the same from either app.
const RUNNABLE = /\.(exe|msi|bat|cmd|com|scr|pif|ps1|vbs|vbe|jse?|wsf|jar|app|dmg|pkg|sh|lnk|hta|html?|svg)$/i;
const MAX_DOC_BYTES = 20 * 1024 ** 3;
const MAX_BATCH = 300;
const MAX_DEPTH = 8;
const isJunk = (name) => name.startsWith('.') || name === 'Thumbs.db';
const segmentsOf = (dir) => dir.split('/').map((x) => x.trim()).filter(Boolean);
/** Which of some files go up, and why each of the rest doesn't. */
function sortOut(list) {
  const ok = [];
  const skipped = [];
  list.forEach((f) => {
    if (isJunk(f.name)) return;
    const why = RUNNABLE.test(f.name) ? 'could run on someone’s computer' : f.size === 0 ? 'is empty' : f.size > MAX_DOC_BYTES ? 'is bigger than 20 GB' : '';
    if (why) skipped.push(`${f.name} ${why}`); else ok.push(f);
  });
  return { ok, skipped };
}
const notAdded = (skipped) => (skipped.length ? `Not added: ${skipped.join('; ')}.` : null);

function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (v < 1024) return `${v} B`;
  const kb = v / 1024; if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024; if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}
const dayOf = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
/** How a file previews here: an image, a video, a frame (PDF, text), or none. */
function previewOf(contentType) {
  const t = String(contentType || '').split(';')[0].trim().toLowerCase();
  if (/^image\/(png|jpe?g|gif|webp|bmp)$/.test(t)) return 'image';
  if (/^video\/(mp4|webm|quicktime)$/.test(t)) return 'video';
  if (t === 'application/pdf' || t === 'text/plain' || t === 'text/csv') return 'frame';
  return null;
}
const CSS = `
  .tdoc-row { display: flex; align-items: center; gap: 8px; width: 100%; min-height: 32px; padding: 5px 10px 5px 12px; border: none;
    border-left: 2.5px solid transparent; background: transparent; font: inherit; font-size: 13.5px; color: var(--text-1);
    text-align: left; cursor: pointer; box-sizing: border-box; }
  .tdoc-row:hover { background: var(--bg-surface-2); }
  .tdoc-row.on { background: var(--accent-soft); color: var(--accent-text); border-left-color: var(--accent); }
  .tdoc-row:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
  .tdoc-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tdoc-line { display: flex; align-items: center; border-left: 2.5px solid transparent; }
  .tdoc-line:hover { background: var(--bg-surface-2); }
  .tdoc-line.on { background: var(--accent-soft); border-left-color: var(--accent); }
  .tdoc-line .tdoc-row, .tdoc-line .tdoc-row.on, .tdoc-line .tdoc-row:hover { background: transparent; border-left-color: transparent; padding-left: 7px; flex: 1; min-width: 0; }
  .tdoc-line.on .tdoc-row { color: var(--accent-text); }
  .tdoc-check { margin: 0 0 0 10px; width: 15px; height: 15px; flex-shrink: 0; accent-color: var(--accent); cursor: pointer; }
  @media (max-width: 520px) { .tdoc-mark-q { display: none; } }
`;

export default function TaskDocuments({ task, confirm, onUploadingChange, canSend = false }) {
  const [data, setData] = useState(null);
  const [path, setPath] = useState([]); // the open folders, top first
  const [picked, setPicked] = useState(null); // a document's id
  const [error, setError] = useState(null);
  const [naming, setNaming] = useState(null); // { mode: 'add' | 'rename', value }
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState([]); // { key, folderId, name, size, sent, state: 'waiting' | 'up' | 'failed', error }
  // key -> { file, folderId, clientActionId, documentId, abort, group, state }: what the queue goes by -- `state` changes
  // here at once ('waiting' | 'up' | 'failed'), as the list on screen only does on its next render.
  const files = useRef(new Map());
  const pumping = useRef(false);
  const fileInput = useRef(null);
  const colsRef = useRef(null);
  const wrapRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const folderInput = useRef(null);
  const [groups, setGroups] = useState([]); // a folder's upload: { id, name, folderId (where it went: null = the top), total, totalBytes, done, doneBytes }
  const [preparing, setPreparing] = useState(null); // "Making its folders…"
  const [renaming, setRenaming] = useState(null); // { id, base, ext }
  const [moving, setMoving] = useState(null); // { id, to }
  const [pctDraft, setPctDraft] = useState(null); // the slider while it's being moved
  const [pctBusy, setPctBusy] = useState(false);
  const [choosing, setChoosing] = useState(false); // Send to Closeout…: its folder picked in the preview's place
  const [bin, setBin] = useState(null); // the task's recycle bin: { on, items, canRestore, error }
  const [binOpen, setBinOpen] = useState(false); // shown in the preview's place
  const [restoring, setRestoring] = useState(null); // an item's id
  const [binNote, setBinNote] = useState(null); // "Restored … to …"

  const load = useCallback(() => getTaskDocuments(task.id)
    .then((d) => { setData(d); if (d && d.error) setError(d.error); return d; })
    .catch((e) => { setData((was) => was || { on: true, folders: [], documents: [] }); setError(e.message || 'Couldn’t load the documents'); return null; }), [task.id]);
  useEffect(() => { load(); }, [load]);
  const loadBin = useCallback(() => getTaskBin(task.id)
    .then((b) => setBin(b && b.on ? { ...b, items: b.items || [] } : null))
    .catch(() => setBin(null)), [task.id]);
  useEffect(() => { loadBin(); }, [loadBin]);

  const active = uploads.some((u) => u.state === 'up' || u.state === 'waiting');
  useEffect(() => { if (onUploadingChange) onUploadingChange(active); }, [active, onUploadingChange]);
  // Closing the window stops what's going (it carries on if the same file goes to the same folder within 7 days).
  useEffect(() => {
    const going = files.current;
    return () => going.forEach((f) => { if (f.abort) f.abort.abort(); });
  }, []);

  // A phone: one column at a time (the open folder's, or the picked file), the path above to go back.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setNarrow(el.clientWidth < 640));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  const folders = useMemo(() => (data && data.folders) || [], [data]);
  const docs = useMemo(() => (data && data.documents) || [], [data]);
  const byId = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  const childrenOf = useCallback((parentId) => folders.filter((f) => (f.parentId || null) === parentId), [folders]);
  const docsIn = useCallback((folderId) => docs.filter((d) => d.folderId === folderId), [docs]);
  // Everything inside a folder, for its removal's warning.
  const inside = useCallback((folderId) => {
    let nFolders = 0; let nDocs = docsIn(folderId).length;
    const walk = (id) => childrenOf(id).forEach((c) => { nFolders += 1; nDocs += docsIn(c.id).length; walk(c.id); });
    walk(folderId);
    return { nFolders, nDocs };
  }, [childrenOf, docsIn]);
  // The open path keeps only folders still there (someone else may have removed one).
  useEffect(() => {
    setPath((p) => {
      const keep = [];
      for (const id of p) { if (!byId.has(id)) break; keep.push(id); }
      return keep.length === p.length ? p : keep;
    });
  }, [byId]);
  const current = path.length ? byId.get(path[path.length - 1]) : null;
  const pickedDoc = picked ? docs.find((d) => d.id === picked) || null : null;
  useEffect(() => { if (picked && data && !pickedDoc) setPicked(null); }, [picked, pickedDoc, data]);
  useEffect(() => { const el = colsRef.current; if (el) el.scrollLeft = el.scrollWidth; }, [path.length, picked]);

  const openFolder = (level, id) => { setPath((p) => [...p.slice(0, level), id]); setPicked(null); setNaming(null); setRenaming(null); setMoving(null); };
  const pickDoc = (level, id) => { setPath((p) => p.slice(0, level)); setPicked(id); setNaming(null); setRenaming(null); setMoving(null); setChoosing(false); setBinOpen(false); };
  /** A folder's chain from the top, its own id last. */
  const chainTo = useCallback((id) => {
    const ids = [];
    for (let f = byId.get(id), i = 0; f && i < 24; f = f.parentId ? byId.get(f.parentId) : null, i += 1) ids.unshift(f.id);
    return ids;
  }, [byId]);

  // Uploads, one file at a time: start (Summit) -> each piece straight to E3 Subs -> finish (Summit).
  const patch = useCallback((key, p) => setUploads((us) => us.map((u) => (u.key === key ? { ...u, ...p } : u))), []);
  const runUpload = useCallback(async (key) => {
    const f = files.current.get(key);
    if (!f) return;
    const { file } = f;
    const abort = new AbortController();
    f.abort = abort;
    f.state = 'up';
    patch(key, { state: 'up', error: null });
    const startBody = {
      folderId: f.folderId, clientActionId: f.clientActionId, name: file.name, size: file.size,
      ...(file.type ? { contentType: file.type } : {}), fileKey: `${file.name}|${file.size}|${file.lastModified}`,
    };
    try {
      let s = await startTaskDocument(task.id, startBody);
      f.documentId = s.document && s.document.id;
      const total = Math.ceil(file.size / s.chunkBytes);
      let idx = s.nextIndex;
      let tries = 0;
      for (let round = 0; round < 4; round += 1) {
        while (idx < total) {
          if (abort.signal.aborted) return;
          patch(key, { sent: Math.min(file.size, idx * s.chunkBytes) });
          const piece = file.slice(idx * s.chunkBytes, Math.min(file.size, (idx + 1) * s.chunkBytes));
          const r = await putDocumentPiece(s.piecesUrl, idx, s.uploadToken, piece, abort.signal);
          if (r.status === 200 && Number.isInteger(r.nextIndex)) { idx = r.nextIndex; tries = 0; continue; }
          if (r.status === 409 && r.code === 'NOT_UPLOADING') throw new Error('That upload was removed.');
          tries += 1;
          if (tries > 5) throw new Error(r.error || 'E3 Subs couldn’t take that piece.');
          if (r.status === 409 && r.code === 'OUT_OF_ORDER' && Number.isInteger(r.nextIndex)) { idx = r.nextIndex; continue; }
          if (r.status === 401 || r.status === 409) { s = await startTaskDocument(task.id, startBody); idx = s.nextIndex; continue; } // its token ran out, or where it carries on
          if (r.status === 400) continue; // the piece arrived wrong: send it again
          await new Promise((ok) => setTimeout(ok, Math.min(30000, 1000 * 2 ** tries))); // not reached: wait, then again
        }
        patch(key, { sent: file.size });
        try {
          await finishTaskDocument(task.id, s.document.id);
          files.current.delete(key);
          setUploads((us) => us.filter((u) => u.key !== key));
          if (f.group) setGroups((gs) => gs.map((g) => (g.id === f.group ? { ...g, done: g.done + 1, doneBytes: g.doneBytes + file.size } : g)).filter((g) => g.done < g.total));
          await load();
          return;
        } catch (e) {
          if (e.code !== 'START_AGAIN' && e.code !== 'PIECES_MISSING') throw e;
          s = await startTaskDocument(task.id, startBody); // where E3 Subs has it
          idx = s.nextIndex;
        }
      }
      throw new Error('The upload didn’t finish. Try again.');
    } catch (e) {
      if (abort.signal.aborted || (e && e.name === 'AbortError')) return;
      f.state = 'failed';
      patch(key, { state: 'failed', error: e.message || 'The upload stopped' });
    }
  }, [task.id, load, patch]);
  const pump = useCallback(async () => {
    if (pumping.current) return;
    pumping.current = true;
    try {
      for (;;) {
        const next = [...files.current.entries()].find(([, x]) => x.state === 'waiting');
        if (!next) break;
        await runUpload(next[0]); // eslint-disable-line no-await-in-loop
      }
    } finally {
      pumping.current = false;
    }
  }, [runUpload]);
  const queue = (pairs, group = null) => { // [{ file, folderId }]
    const added = pairs.map(({ file, folderId }) => {
      const key = newRequestId();
      files.current.set(key, { file, folderId, clientActionId: newRequestId(), documentId: null, abort: null, group, state: 'waiting' });
      return { key, folderId, name: file.name, size: file.size, sent: 0, state: 'waiting', error: null, group };
    });
    setUploads((us) => [...us, ...added]);
    setTimeout(pump, 0);
  };
  const addFiles = (list) => {
    if (!current || !list || !list.length) return;
    const { ok, skipped } = sortOut([...list]);
    setError(notAdded(skipped));
    if (ok.length) queue(ok.map((file) => ({ file, folderId: current.id })));
  };
  // A whole folder (Upload folder): its files go into folders of the same names, made inside the open one -- or as a
  // section at the top, a top folder of a built-in's name going into it. Everything's checked before anything's made.
  const ensureFolder = async (parentId, name, cache, list) => {
    const k = `${parentId || '~'}/${name.toLowerCase()}`;
    if (cache.has(k)) return cache.get(k);
    const same = (f) => (f.parentId || null) === parentId && f.name.toLowerCase() === name.toLowerCase();
    let found = list.find(same);
    if (!found) {
      try {
        found = (await addTaskFolder(task.id, name, parentId)).folder;
      } catch (e) {
        const again = await getTaskDocuments(task.id).catch(() => null);
        found = again && (again.folders || []).find(same);
        if (!found) throw e;
      }
    }
    cache.set(k, found.id);
    return found.id;
  };
  const addFolder = async (list) => {
    const picked = [...(list || [])].filter((f) => !isJunk(f.name)).map((file) => {
      const rel = file.webkitRelativePath || '';
      return { file, dir: rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : '' };
    });
    setError(null);
    if (!picked.length) { setError('Nothing to upload: that folder has no files in it.'); return; }
    if (picked.length > MAX_BATCH) { setError(`That’s ${picked.length} files. Upload at most ${MAX_BATCH} at a time.`); return; }
    const targetId = current ? current.id : null;
    if (!targetId && picked.some((p) => !p.dir)) { setError('Files go in a folder: open one first, or upload a whole folder to make it a section.'); return; }
    const depth = targetId ? chainTo(targetId).length : 0;
    const deepest = Math.max(...picked.map((p) => segmentsOf(p.dir).length));
    if (depth + deepest > MAX_DEPTH) { setError(`Folders go ${MAX_DEPTH} deep at most, and that one would go ${depth + deepest} deep here.`); return; }
    const long = picked.flatMap((p) => segmentsOf(p.dir)).find((x) => x.length > 80);
    if (long) { setError(`Folder names are 80 characters at most: “${long.slice(0, 40)}…” is longer.`); return; }
    const byDir = new Map();
    picked.forEach((p) => byDir.set(p.dir, [...(byDir.get(p.dir) || []), p.file]));
    const skipped = [];
    const going = [];
    byDir.forEach((fs, dir) => { const r = sortOut(fs); skipped.push(...r.skipped); if (r.ok.length) going.push([dir, r.ok]); });
    if (!going.length) { setError(notAdded(skipped) || 'Nothing to upload.'); return; }
    setPreparing('Making its folders…');
    try {
      const fresh = await getTaskDocuments(task.id).catch(() => null);
      const list0 = (fresh && fresh.folders) || folders;
      const cache = new Map();
      const pairs = [];
      for (const [dir, fs] of going) {
        let parent = targetId;
        for (const seg of segmentsOf(dir)) parent = await ensureFolder(parent, seg, cache, list0); // eslint-disable-line no-await-in-loop
        fs.forEach((file) => pairs.push({ file, folderId: parent }));
      }
      await load();
      const top = segmentsOf(going[0][0])[0] || 'The folder';
      const id = newRequestId();
      setGroups((gs) => [...gs, { id, name: top, folderId: targetId, total: pairs.length, totalBytes: pairs.reduce((n, x) => n + x.file.size, 0), done: 0, doneBytes: 0 }]);
      queue(pairs, id);
      setError(notAdded(skipped));
    } catch (e) {
      setError(e.message || 'Its folders weren’t made. Try again.');
    } finally {
      setPreparing(null);
    }
  };
  const retry = (key) => {
    const f = files.current.get(key);
    if (f) f.state = 'waiting';
    patch(key, { state: 'waiting', error: null });
    setTimeout(pump, 0);
  };
  const stop = async (key) => {
    const f = files.current.get(key);
    if (f && f.abort) f.abort.abort();
    files.current.delete(key);
    setUploads((us) => us.filter((u) => u.key !== key));
    if (f && f.documentId) await removeTaskDocument(task.id, f.documentId).catch(() => {}); // E3 Subs drops it after 7 days anyway
    setTimeout(pump, 0);
  };
  // A folder's upload stopped: every file of it still to go.
  const stopGroup = (id) => {
    [...files.current.entries()].filter(([, f]) => f.group === id).forEach(([key]) => stop(key));
    setGroups((gs) => gs.filter((g) => g.id !== id));
  };

  const act = async (run) => {
    setBusy(true);
    setError(null);
    try {
      return await run();
    } catch (e) {
      setError(e.message || 'That didn’t go through');
      return null;
    } finally {
      setBusy(false);
    }
  };
  const saveName = async () => {
    const name = String((naming && naming.value) || '').replace(/\s+/g, ' ').trim();
    if (!name) return;
    if (naming.mode === 'add') {
      const r = await act(() => addTaskFolder(task.id, name, current ? current.id : null));
      if (r && r.folder) { await load(); setNaming(null); setPath((p) => [...p, r.folder.id]); }
    } else {
      const r = await act(() => renameTaskFolder(task.id, current.id, name));
      if (r) { await load(); setNaming(null); }
    }
  };
  const removeFolder = async () => {
    const { nFolders, nDocs } = inside(current.id);
    const what = [nDocs ? `${nDocs} document${nDocs === 1 ? '' : 's'}` : null, nFolders ? `${nFolders} folder${nFolders === 1 ? '' : 's'}` : null].filter(Boolean).join(' and ');
    const ok = await confirm({
      title: `Remove the folder ${current.name}?`,
      message: `It${what ? ` and what’s in it (${what})` : ''} come${what ? '' : 's'} off the list here and on E3 Subs, into E3 Subs’ recycle bin. E3’s admins can restore it there for 365 days.`,
      confirmLabel: 'Remove', danger: true,
    });
    if (!ok) return;
    const r = await act(() => removeTaskFolder(task.id, current.id));
    if (r) { setPath((p) => p.slice(0, -1)); await load(); loadBin(); }
  };
  const removeDoc = async (d) => {
    const ok = await confirm({
      title: `Remove ${d.name}?`,
      message: 'It comes off the list here and on E3 Subs, into E3 Subs’ recycle bin. E3’s admins can restore it there for 365 days.',
      confirmLabel: 'Remove', danger: true,
    });
    if (!ok) return;
    const r = await act(() => removeTaskDocument(task.id, d.id));
    if (r) { setPicked(null); await load(); loadBin(); }
  };
  // Rename (the ending stays: it says what kind of file it is) and Move, as on E3 Subs.
  const startRename = (d) => {
    const dot = d.name.lastIndexOf('.');
    setRenaming(dot > 0 ? { id: d.id, base: d.name.slice(0, dot), ext: d.name.slice(dot) } : { id: d.id, base: d.name, ext: '' });
    setMoving(null);
  };
  const saveRename = async (d) => {
    const name = `${renaming.base.replace(/\s+/g, ' ').trim()}${renaming.ext}`;
    if (!renaming.base.trim() || name === d.name) { setRenaming(null); return; }
    const r = await act(() => renameTaskDocument(task.id, d.id, name));
    if (r) { setRenaming(null); await load(); }
  };
  const saveMove = async (d) => {
    if (!moving.to || moving.to === d.folderId) { setMoving(null); return; }
    const r = await act(() => moveTaskDocument(task.id, d.id, moving.to));
    if (r) { const to = moving.to; setMoving(null); await load(); setPath(chainTo(to)); }
  };
  /** The task's folders as a tree, top-down, each with how deep it sits. */
  const tree = useMemo(() => {
    const out = [];
    const walk = (parentId, deep) => folders.filter((f) => (f.parentId || null) === parentId).forEach((f) => { out.push({ f, deep }); walk(f.id, deep + 1); });
    walk(null, 0);
    return out;
  }, [folders]);
  // To Closeout: what's ticked, and the send (its folder picked in the preview's place).
  const ticks = useTicks(folders, docs);
  const sending = useCloseoutSend(task.project_id);
  const sendCount = useMemo(() => docs.filter((d) => ticks.ticked.has(`document:${task.id}:${d.id}`) || ticks.covered(d.folderId, task.id)).length,
    [docs, ticks.ticked, ticks.covered, task.id]);
  const sendTo = (folderId) => sending.start(ticks.items, folderId).then((s) => { setChoosing(false); if (s) ticks.clear(); });
  // The slider: saved when it's let go (and each arrow-key step), shown as what E3 Subs answers.
  const savePct = async () => {
    if (pctDraft == null) return;
    const v = pctDraft;
    const was = data && data.percent ? data.percent.value : null;
    if (v === was) { setPctDraft(null); return; }
    setPctBusy(true);
    setError(null);
    try {
      const r = await setTaskDocumentsPercent(task.id, v);
      setData((d) => ({ ...d, percent: (r && r.percent) || { value: v, by: null, at: new Date().toISOString() } }));
    } catch (e) {
      setError(e.message || 'The progress wasn’t saved');
    } finally {
      setPctBusy(false);
      setPctDraft(null);
    }
  };
  // Its links last 15 minutes: read the list again for fresh ones when they're about to run out.
  const linkOf = async (d, kind) => {
    let doc = d;
    if (!doc.linksExpireAt || Date.parse(doc.linksExpireAt) - Date.now() < 60000) {
      const fresh = await load();
      doc = (fresh && (fresh.documents || []).find((x) => x.id === d.id)) || doc;
    }
    return kind === 'open' ? doc.openUrl : doc.downloadUrl;
  };
  const open = async (d) => { const url = await linkOf(d, 'open'); if (url) window.open(url, '_blank', 'noopener'); };
  const download = async (d) => {
    const url = await linkOf(d, 'download');
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!data) return <div style={{ ...muted, padding: '10px 0' }}>Loading the documents…</div>;
  if (!data.on) return <div style={{ ...muted, padding: '10px 0' }}>Documents aren’t connected to E3 Subs yet.</div>;

  const whoOf = (d) => `${(d.addedBy && d.addedBy.name) || 'Someone'} · ${d.addedBy && d.addedBy.by === 'e3' ? 'E3' : (data.sub && data.sub.name) || 'the sub'}`;
  const countOf = (f) => childrenOf(f.id).length + docsIn(f.id).length;
  // The columns: the folders at the top, then each open folder's folders, files and uploads.
  const columns = [{ level: 0, title: 'Folders', folder: null }, ...path.map((id, i) => ({ level: i + 1, title: (byId.get(id) || {}).name || '…', folder: byId.get(id) }))];
  const shownColumns = narrow ? (pickedDoc || choosing || binOpen ? [] : columns.slice(-1)) : columns;
  // A row with its tick (for someone who can send to Closeout; anyone else sees the row alone).
  const ticked = (kind, f, inside, row, on) => (!canSend ? row : (
    <div key={f.id} className={`tdoc-line${on ? ' on' : ''}`}>
      <input type="checkbox" className="tdoc-check" checked={inside || ticks.ticked.has(`${kind}:${task.id}:${f.id}`)} disabled={inside}
        onChange={() => ticks.toggle(kind, f.id, task.id)} aria-label={`Pick ${f.name}${kind === 'folder' ? ', everything in it' : ''}`} />
      {row}
    </div>
  ));
  const column = (c) => {
    const subFolders = childrenOf(c.folder ? c.folder.id : null);
    const fileRows = c.folder ? docsIn(c.folder.id) : [];
    const ups = c.folder ? uploads.filter((u) => u.folderId === c.folder.id) : [];
    const gs = groups.filter((g) => (g.folderId || null) === (c.folder ? c.folder.id : null));
    const n = subFolders.length + fileRows.length;
    return (
      <div key={c.level} role="group" aria-label={c.title}
        style={{ flex: `0 0 ${narrow ? '100%' : `${COL_W}px`}`, borderRight: '1px solid var(--border-subtle)', overflowY: 'auto', paddingBottom: 6 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)', padding: '9px 12px 6px', display: 'flex', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
          <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>{n} item{n === 1 ? '' : 's'}</span>
        </div>
        {subFolders.map((f) => ticked('folder', f, ticks.covered(f.parentId, task.id), (
          <button key={f.id} type="button" className={`tdoc-row${path[c.level] === f.id ? ' on' : ''}`} title={f.name} onClick={() => openFolder(c.level, f.id)}>
            <Glyph>{FOLDER}</Glyph>
            <span className="tdoc-name">{f.name}</span>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>{countOf(f)}</span>
            <Glyph size={12}>{CHEVRON}</Glyph>
          </button>
        ), path[c.level] === f.id))}
        {fileRows.map((d) => ticked('document', d, ticks.covered(d.folderId, task.id), (
          <button key={d.id} type="button" className={`tdoc-row${picked === d.id ? ' on' : ''}`} title={d.name} onClick={() => pickDoc(c.level, d.id)}>
            <Glyph>{FILE}</Glyph>
            <span className="tdoc-name">{d.name}</span>
            {d.photoReport && <AutoTag />}
          </button>
        ), picked === d.id))}
        {ups.map((u) => {
          const pct = u.size ? Math.min(100, Math.round((u.sent / u.size) * 100)) : 0;
          return (
            <div key={u.key} style={{ padding: '6px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-2)' }}>
                <Glyph>{FILE}</Glyph><span className="tdoc-name">{u.name}</span>
              </div>
              <div style={{ ...muted, fontSize: 12, margin: '2px 0 4px 23px' }}>
                {u.state === 'failed' ? <span style={{ color: 'var(--danger)' }}>{u.error}</span>
                  : u.state === 'waiting' ? 'Waiting to upload' : `Uploading ${pct}% · ${formatBytes(u.sent)} of ${formatBytes(u.size)}`}
              </div>
              <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', marginLeft: 23 }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: u.state === 'failed' ? 'var(--danger)' : 'var(--accent)' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, margin: '4px 0 0 23px' }}>
                {u.state === 'failed' && <button type="button" style={linkBtn} onClick={() => retry(u.key)}>Try again</button>}
                <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => stop(u.key)}>{u.state === 'failed' ? 'Drop it' : 'Stop'}</button>
              </div>
            </div>
          );
        })}
        {gs.map((g) => {
          const going = uploads.filter((u) => u.group === g.id);
          const failed = going.filter((u) => u.state === 'failed');
          const sent = Math.min(g.totalBytes, g.doneBytes + going.reduce((t, u) => t + (u.state === 'up' ? u.sent : 0), 0));
          const pct = g.totalBytes ? Math.round((sent / g.totalBytes) * 100) : 0;
          return (
            <div key={g.id} style={{ padding: '6px 12px', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-2)' }}>
                <Glyph>{FOLDER}</Glyph><span className="tdoc-name">{g.name}</span>
              </div>
              <div style={{ ...muted, fontSize: 12, margin: '2px 0 4px 23px' }}>
                {`Uploading ${Math.min(g.total, g.done + 1)} of ${g.total} file${g.total === 1 ? '' : 's'} · ${formatBytes(sent)} of ${formatBytes(g.totalBytes)}`}
                {failed.length > 0 && <span style={{ color: 'var(--danger)' }}>{` · ${failed.length} didn’t go up`}</span>}
              </div>
              <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', marginLeft: 23 }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: 'var(--accent)' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, margin: '4px 0 0 23px' }}>
                {failed.length > 0 && <button type="button" style={linkBtn} onClick={() => failed.forEach((u) => retry(u.key))}>Try again</button>}
                <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => stopGroup(g.id)}>Stop</button>
              </div>
            </div>
          );
        })}
        {c.folder && !n && !ups.length && <div style={{ ...muted, padding: '6px 12px' }}>Empty. Upload files here, or add a folder.</div>}
      </div>
    );
  };
  // The recycle bin, in the preview's place: what was removed here, each with where it goes back to and Restore.
  const pathOf = (item) => (Array.isArray(item.path) ? item.path.join(' › ') : item.path) || 'the top';
  const restore = async (item) => {
    setRestoring(item.id);
    setError(null);
    setBinNote(null);
    try {
      await restoreFromTaskBin(task.id, item.kind, item.id);
      setBinNote(`Restored “${item.name}” to ${pathOf(item)}.`);
      await load();
      await loadBin();
    } catch (e) {
      setError(e.message || 'It wasn’t restored');
    } finally {
      setRestoring(null);
    }
  };
  const binPane = () => {
    const items = (bin && bin.items) || [];
    const who = (by) => (by ? `${by.name || 'Someone'} (${(by.by || by.kind) === 'e3' ? 'E3' : (data.sub && data.sub.name) || 'the sub'})` : 'someone');
    const insideOf = (item) => {
      const inside = item.inside || { folders: item.folders, documents: item.documents };
      const parts = [Number(inside.documents) ? `${inside.documents} file${Number(inside.documents) === 1 ? '' : 's'}` : null,
        Number(inside.folders) ? `${inside.folders} folder${Number(inside.folders) === 1 ? '' : 's'}` : null].filter(Boolean);
      return parts.length ? `${parts.join(' and ')} inside` : 'empty';
    };
    return (
      <div style={{ flex: 1, minHeight: 200, borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 12px 8px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)' }}>Recycle bin</span>
          <span style={muted}>this task · kept {(bin && bin.keepDays) || 365} days</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setBinOpen(false)}>Close</button>
        </div>
        {binNote && <div style={{ margin: '8px 12px 0', fontSize: 13, color: 'var(--success)' }} role="status">{binNote}</div>}
        {bin && bin.error && <div style={{ margin: '8px 12px 0' }}><ErrorBanner>{bin.error}</ErrorBanner></div>}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!items.length && <div style={{ ...muted, padding: '10px 12px' }}>Nothing in the bin.</div>}
          {items.map((item) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}><Glyph>{item.kind === 'folder' ? FOLDER : FILE}</Glyph></span>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: 'var(--text-1)', minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</span>
                  {item.photoReport && <AutoTag />}
                </div>
                <div style={{ ...muted, fontSize: 12 }}>
                  {[`From ${pathOf(item)}`, item.kind === 'folder' ? insideOf(item) : formatBytes(item.size), `removed by ${who(item.removedBy)}`,
                    dayOf(item.removedAt), item.purgesOn ? `gone ${dayOf(item.purgesOn)}` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              {bin.canRestore && (
                <SecondaryButton busy={restoring === item.id} disabled={!!restoring && restoring !== item.id} onClick={() => restore(item)} style={{ height: 30, fontSize: 13 }}>
                  Restore
                </SecondaryButton>
              )}
            </div>
          ))}
        </div>
        <div style={{ ...muted, fontSize: 12, padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
          {bin && bin.canRestore
            ? 'Restore puts it back in its folder (and brings back that folder if it’s in the bin too). Delete forever is on E3 Subs’ bin page, for E3 admins.'
            : 'Restoring is for admins, VPs and accounting, and the people they give it to.'}
        </div>
      </div>
    );
  };
  const kind = pickedDoc ? previewOf(pickedDoc.contentType) : null;

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
      <style>{CSS}</style>
      {data.documentsTask === false && (
        <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13.5, color: 'var(--warning)', background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)' }}>
          E3 Subs doesn’t have this as a Documents task yet. It picks up the type from the Schedule within a minute.{' '}
          <button type="button" style={linkBtn} onClick={load}>Check again</button>
        </div>
      )}
      {/* The documents' progress: one slider, the sub's and E3's (Duncan 2026-10-25). Duncan 2026-10-26 (the mockup he
          OK'd, as on E3 Subs' card): what to do with it and what it does always shown, marks under the track, and who
          moved it last on a quieter line. */}
      {(() => {
        const saved = data.percent || null;
        const shown = pctDraft != null ? pctDraft : saved ? saved.value : 0;
        const by = saved && saved.by;
        const movable = data.documentsTask !== false;
        let at = '';
        if (saved && saved.at) {
          const d = new Date(saved.at);
          const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
          const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          at = days === 0 ? `Today ${time}` : days === 1 ? `Yesterday ${time}`
            : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) })}, ${time}`;
        }
        // The native thumb is about 16px wide: a mark sits where the thumb's middle is at that value.
        const mark = (p) => (p === 0 ? { left: 0 } : p === 100 ? { right: 0 } : { left: `calc(8px + (100% - 16px) * ${p / 100})`, transform: 'translateX(-50%)' });
        return (
          <div style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
            {movable && <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginBottom: 6 }}>Drag the slider to show how much of this task’s documents are in.</div>}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px 12px' }}>
              <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-1)', lineHeight: '20px' }}>Progress</span>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <input type="range" min={0} max={100} step={5} value={shown} aria-label="The documents’ progress" disabled={pctBusy || !movable}
                  onChange={(e) => setPctDraft(Number(e.target.value))} onPointerUp={savePct} onKeyUp={savePct} onBlur={savePct}
                  style={{ display: 'block', width: '100%', margin: '2px 0 0', accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <div aria-hidden="true" style={{ position: 'relative', height: 15, marginTop: 2 }}>
                  {[0, 25, 50, 75, 100].map((p) => (
                    <span key={p} className={p === 25 || p === 75 ? 'tdoc-mark-q' : undefined} // a phone keeps 0, 50 and 100
                      style={{ position: 'absolute', top: 0, fontSize: 11, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...mark(p) }}>{p}%</span>
                  ))}
                </div>
              </div>
              <span className="mono" style={{ fontSize: 14, fontWeight: 650, minWidth: 42, color: 'var(--text-1)', lineHeight: '20px' }}>{shown}%</span>
            </div>
            <div style={{ ...muted, marginTop: 4 }}>
              It fills this task’s bar on the Subcontractors tab and in E3 Subs, where the sub sees it and can move it too. It doesn’t change the task’s % complete.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 4 }}>
              {!saved ? 'Not set yet' : `Last set by ${by ? `${by.name} (${by.by === 'e3' ? 'E3' : (data.sub && data.sub.name) || 'the sub'})` : 'you'}${at ? ` · ${at}` : ''}`}
            </div>
          </div>
        );
      })()}
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {/* The path, and what to do here */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 32 }}>
        <nav aria-label="Folder path" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13.5, minWidth: 0 }}>
          <button type="button" style={{ ...linkBtn, color: path.length || picked ? 'var(--accent-text)' : 'var(--text-1)' }} onClick={() => { setPath([]); setPicked(null); setNaming(null); }}>Documents</button>
          {path.map((id, i) => (
            <React.Fragment key={id}>
              <span style={{ color: 'var(--text-4)' }}><Glyph size={12}>{CHEVRON}</Glyph></span>
              <button type="button" style={{ ...linkBtn, color: i === path.length - 1 && !picked ? 'var(--text-1)' : 'var(--accent-text)' }}
                onClick={() => { setPath((p) => p.slice(0, i + 1)); setPicked(null); setNaming(null); }}>{(byId.get(id) || {}).name}</button>
            </React.Fragment>
          ))}
        </nav>
        <span style={{ flex: 1 }} />
        {naming ? (
          <form onSubmit={(e) => { e.preventDefault(); saveName(); }} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input autoFocus value={naming.value} maxLength={80} aria-label={naming.mode === 'add' ? 'The new folder’s name' : 'The folder’s new name'}
              placeholder={naming.mode === 'add' ? (current ? `A folder in ${current.name}` : 'A section, e.g. Fixture cut sheets') : ''}
              onChange={(e) => setNaming({ ...naming, value: e.target.value })} onKeyDown={(e) => { if (e.key === 'Escape') setNaming(null); }}
              style={{ ...fieldStyle, height: 32, width: 240 }} />
            <SecondaryButton type="submit" busy={busy} style={{ height: 32, fontSize: 13 }}>{naming.mode === 'add' ? 'Add' : 'Rename'}</SecondaryButton>
            <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setNaming(null)}>Cancel</button>
          </form>
        ) : (
          <>
            <SecondaryButton onClick={() => setNaming({ mode: 'add', value: '' })} style={{ height: 32, fontSize: 13 }}
              title={current ? `A folder inside ${current.name}` : 'A section at the top'}>
              <Glyph size={14}>{FOLDER}</Glyph>New folder
            </SecondaryButton>
            {current && (
              <SecondaryButton onClick={() => fileInput.current && fileInput.current.click()} style={{ height: 32, fontSize: 13 }}>
                <Glyph size={14}><path d="M12 20V8" /><path d="m7.5 12.5 4.5-4.5 4.5 4.5" /><path d="M4.5 4.5h15" /></Glyph>Upload files
              </SecondaryButton>
            )}
            <SecondaryButton busy={!!preparing} onClick={() => folderInput.current && folderInput.current.click()} style={{ height: 32, fontSize: 13 }}
              title={current ? `A whole folder into ${current.name}, its folders and all` : 'A whole folder, as a section at the top'}>
              <Glyph size={14}>{FOLDER}</Glyph>Upload folder
            </SecondaryButton>
            {current && !current.builtIn && (
              <>
                <button type="button" style={linkBtn} onClick={() => setNaming({ mode: 'rename', value: current.name })}>Rename</button>
                <button type="button" style={{ ...linkBtn, color: 'var(--danger)' }} onClick={removeFolder}>Remove folder</button>
              </>
            )}
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory="" onChange={(e) => { addFolder(e.target.files); e.target.value = ''; }} />
            {bin && bin.items.length > 0 && (
              <button type="button" style={{ ...linkBtn, display: 'inline-flex', alignItems: 'center', gap: 4 }} aria-pressed={binOpen}
                onClick={() => { setBinOpen(true); setPicked(null); setChoosing(false); setMoving(null); setRenaming(null); setBinNote(null); loadBin(); }}>
                <Glyph size={14}>{TRASH}</Glyph>Recycle bin · {bin.items.length}
              </button>
            )}
          </>
        )}
      </div>
      {preparing && <div style={{ ...muted, marginTop: -4 }}>{preparing}</div>}
      {!current && !naming && !preparing && <div style={{ ...muted, marginTop: -4 }}>Open a folder to upload files into it. Here, New folder or Upload folder adds a section beside the five.</div>}
      {/* To Closeout (Duncan 2026-10-25): what's ticked, then Send to Closeout… */}
      {canSend && ticks.items.length > 0 && !sending.ask && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '6px 8px 6px 12px', borderRadius: 10, border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)', background: 'var(--accent-soft)' }}>
          <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-1)' }}>{ticks.items.length} selected</span>
          <span style={muted}>{sendCount === 1 ? '1 file' : `${sendCount} files`}</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => { ticks.clear(); setChoosing(false); }}>Clear</button>
          <SecondaryButton disabled={!sendCount} onClick={() => { setChoosing(true); setBinOpen(false); setPicked(null); setMoving(null); setRenaming(null); }}
            style={{ height: 30, fontSize: 13, color: 'var(--accent-text)', borderColor: 'color-mix(in oklch, var(--accent) 45%, transparent)' }}>
            <Glyph size={14}>{SEND}</Glyph>Send to Closeout…
          </SecondaryButton>
        </div>
      )}
      {canSend && <AlreadyThere ask={sending.ask} busy={sending.busy} onAnswer={(c) => sending.answer(c).then((s) => { if (s) ticks.clear(); })} />}
      {canSend && <SendProgress send={sending.send} error={sending.error} busy={sending.busy} onAgain={sending.again} onDismiss={sending.dismiss} />}

      {/* As big as the window (Duncan 2026-10-24: "Why is the preview so small on such a large screen?"): the window takes
          90% of the screen's height and this fills what's left of it. */}
      <div ref={colsRef} style={{ display: 'flex', border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)', flex: 1, minHeight: 300, overflowX: 'auto' }}>
        {shownColumns.map(column)}
        {choosing && (
          <div style={{ flex: narrow ? '0 0 100%' : '1 0 320px', minWidth: narrow ? 0 : 320, padding: 12, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CloseoutFolderPicker projectId={task.project_id} count={sendCount === 1 ? '1 file' : `${sendCount} files`} busy={sending.busy}
              onPick={(folderId) => sendTo(folderId)} onCancel={() => setChoosing(false)} />
          </div>
        )}
        {binOpen && !choosing && (
          <div style={{ flex: narrow ? '0 0 100%' : '1 0 320px', minWidth: narrow ? 0 : 320, padding: 12, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {binPane()}
          </div>
        )}
        {!choosing && !binOpen && !pickedDoc && !narrow && (
          <div style={{ flex: '1 0 280px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...muted }}>
            {current ? 'Pick a file to see it here.' : 'Open a folder, then pick a file to see it here.'}
          </div>
        )}
        {!choosing && !binOpen && pickedDoc && (
          <div style={{ flex: narrow ? '0 0 100%' : '1 0 320px', minWidth: narrow ? 0 : 320, padding: 12, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
            {/* The file's name and details on two lines with its buttons beside them, the preview filling the rest (a
                short screen keeps its height for the preview). */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px 12px', flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                {renaming && renaming.id === pickedDoc.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); saveRename(pickedDoc); }} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input autoFocus value={renaming.base} maxLength={255 - renaming.ext.length} aria-label="The file’s new name"
                      onChange={(e) => setRenaming({ ...renaming, base: e.target.value })} onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                      style={{ ...fieldStyle, height: 30, flex: '1 1 160px', minWidth: 0 }} />
                    {renaming.ext && <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{renaming.ext}</span>}
                    <SecondaryButton type="submit" busy={busy} style={{ height: 30, fontSize: 13 }}>Rename</SecondaryButton>
                    <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setRenaming(null)}>Cancel</button>
                  </form>
                ) : (
                  <div title={pickedDoc.name} style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pickedDoc.name}</div>
                )}
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>{[formatBytes(pickedDoc.size), whoOf(pickedDoc), dayOf(pickedDoc.addedAt)].filter(Boolean).join(' · ')}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pickedDoc.openUrl && <SecondaryButton onClick={() => open(pickedDoc)} style={{ height: 30, fontSize: 13 }}>Open</SecondaryButton>}
                <SecondaryButton onClick={() => download(pickedDoc)} style={{ height: 30, fontSize: 13 }}>Download</SecondaryButton>
                <SecondaryButton onClick={() => startRename(pickedDoc)} style={{ height: 30, fontSize: 13 }}>Rename</SecondaryButton>
                <SecondaryButton onClick={() => { setRenaming(null); setMoving(moving && moving.id === pickedDoc.id ? null : { id: pickedDoc.id, to: null }); }} style={{ height: 30, fontSize: 13 }}>Move</SecondaryButton>
                <SecondaryButton danger busy={busy} onClick={() => removeDoc(pickedDoc)} style={{ height: 30, fontSize: 13 }}>Remove</SecondaryButton>
              </div>
            </div>
            {moving && moving.id === pickedDoc.id ? (
              <div style={{ flex: 1, minHeight: 200, borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ ...muted, padding: '9px 12px 6px' }}>Move <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{pickedDoc.name}</strong> to…</div>
                <div role="listbox" aria-label="The folder to move it to" style={{ flex: 1, overflowY: 'auto' }}>
                  {tree.map(({ f, deep }) => {
                    const here = f.id === pickedDoc.folderId;
                    return (
                      <button key={f.id} type="button" role="option" aria-selected={moving.to === f.id} disabled={here}
                        className={`tdoc-row${moving.to === f.id ? ' on' : ''}`} onClick={() => setMoving({ ...moving, to: f.id })}
                        style={{ paddingLeft: 12 + deep * 18, color: here ? 'var(--text-4)' : undefined, cursor: here ? 'default' : 'pointer' }}>
                        <Glyph>{FOLDER}</Glyph>
                        <span className="tdoc-name">{f.name}{here ? ' (here now)' : ''}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                  <SecondaryButton busy={busy} disabled={!moving.to} onClick={() => saveMove(pickedDoc)} style={{ height: 30, fontSize: 13 }}>Move here</SecondaryButton>
                  <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setMoving(null)}>Cancel</button>
                </div>
              </div>
            ) : (
            <div style={{ flex: 1, minHeight: 200, borderRadius: 8, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {kind === 'image' && pickedDoc.openUrl && <img src={pickedDoc.openUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
              {kind === 'video' && pickedDoc.openUrl && <video src={pickedDoc.openUrl} controls style={{ maxWidth: '100%', maxHeight: '100%' }} />}
              {kind === 'frame' && pickedDoc.openUrl && <iframe src={pickedDoc.openUrl} title={pickedDoc.name} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />}
              {(!kind || !pickedDoc.openUrl) && <span style={{ color: 'var(--text-4)' }}><Glyph size={36}>{FILE}</Glyph></span>}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
